import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { formatPhone } from '../../shared/phone';
import { formatShortDateTime } from '../../shared/time';
import {
  Avatar,
  Callout,
  EmptyState,
  IconExternal,
  IconLink,
  Pagination,
  Skeleton,
  SummaryFact,
  SummaryFacts,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { phoneGlyph } from '../SmsInbox/smsModel';
import ReservationsList from './ReservationsHeader';
import {
  DEFAULT_LINKS_ORDERING,
  fetchReservationLinksPage,
  LINKS_SORT_FIELD_TO_UI,
  LINKS_SORT_UI_TO_FIELD,
  nextOrdering,
  parseOrdering,
  RESERVATIONS_PAGE_SIZE,
} from './reservationModel';

const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  sortable?: boolean;
}[] = [
  { key: 'created_at', label: 'Date & time', width: 160, sortable: true },
  { key: 'customer_id', label: 'Customer ID', width: 120, sortable: true },
  { key: 'customer_name', label: 'Customer', width: 200, sortable: true },
  { key: 'customer_phone', label: 'Phone', width: 152 },
  { key: 'reservation_link', label: 'Reservation link' },
];

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

const ReservationLinksView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam = searchParams.get('ordering') ?? DEFAULT_LINKS_ORDERING;
  const [currentPage, setCurrentPage] = useState(1);

  const { sortKey, sortOrder } = useMemo(
    () => parseOrdering(orderingParam, LINKS_SORT_FIELD_TO_UI, 'created_at'),
    [orderingParam],
  );

  const queryKey = useScopedQueryKey(
    'reservation-links',
    currentPage,
    RESERVATIONS_PAGE_SIZE,
    orderingParam,
  );
  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchReservationLinksPage(
        currentPage,
        RESERVATIONS_PAGE_SIZE,
        orderingParam,
      ),
    placeholderData: keepPreviousData,
  });

  const links = data?.reservations ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / RESERVATIONS_PAGE_SIZE),
  );
  const showInitialLoading = isPending && links.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'An unexpected error occurred. Please try again.'
        : null;
  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && links.length > 0;
  const showPagination = !showInitialLoading && !fetchError && links.length > 0;
  const showCount = showInitialLoading || totalCount > 0;

  const toggleSort = useCallback(
    (key: string) => {
      const targetField = LINKS_SORT_UI_TO_FIELD[key];
      if (!targetField) return;
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(
          'ordering',
          nextOrdering(
            prev.get('ordering') ?? DEFAULT_LINKS_ORDERING,
            targetField,
          ),
        );
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <ReservationsList
      refreshLabel={isRefreshing ? 'Updating reservation links...' : null}
      summary={
        showCount && (
          <SummaryFacts>
            <SummaryFact
              label="Reservation links"
              value={
                showInitialLoading ? (
                  <Skeleton width={48} className="h-7" />
                ) : (
                  totalCount.toLocaleString()
                )
              }
            />
          </SummaryFacts>
        )
      }
      toolbar={
        <p className="max-w-[52ch] text-v2-12 leading-[18px] text-v2-fg-3">
          Booking links Arthur sent to guests by text so they could finish a
          reservation themselves.
        </p>
      }
    >
      {fetchError ? (
        <div className="p-4 lg:p-6">
          <Callout tone="danger" title="Couldn't load reservation links">
            {fetchError}
          </Callout>
        </div>
      ) : !showInitialLoading && links.length === 0 ? (
        <EmptyState
          icon={<IconLink />}
          title="You don't have any reservation links at the moment"
          description="When reservation links appear, they'll show up here."
        />
      ) : (
        <>
          {showInitialLoading && (
            <p role="status" className="sr-only">
              Loading reservation links…
            </p>
          )}
          <div
            className={clsx(
              'v2-scroll v2-scroll-x transition-opacity',
              isRefreshing && 'opacity-70',
            )}
          >
            <Table fixed className="min-w-[880px]">
              <THead>
                <tr>
                  {COLUMNS.map((column) => (
                    <TH
                      key={column.key}
                      width={column.width}
                      sortable={column.sortable && !showInitialLoading}
                      active={column.sortable && sortKey === column.key}
                      dir={sortOrder}
                      onSort={() => toggleSort(column.key)}
                    >
                      {column.label}
                    </TH>
                  ))}
                </tr>
              </THead>
              <TBody>
                {showInitialLoading ? (
                  <TableSkeletonRows cols={COLUMNS.length} size="lg" />
                ) : (
                  links.map((link, idx) => (
                    <TR size="lg" key={idx}>
                      <TD size="lg" num className="whitespace-nowrap">
                        {formatShortDateTime(link.created_at) || (
                          <MissingValue />
                        )}
                      </TD>
                      <TD size="lg" num className="text-v2-12 text-v2-fg-3">
                        {link.customer_id ?? <MissingValue />}
                      </TD>
                      <TD size="lg" className="overflow-hidden">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <Avatar
                            name={link.customer_name}
                            identity={link.customer_phone}
                            fallback={phoneGlyph(link.customer_phone)}
                            hue="auto"
                          />
                          {link.customer_name ? (
                            <span className="truncate text-v2-14 font-semibold text-v2-fg">
                              {link.customer_name}
                            </span>
                          ) : (
                            <MissingValue />
                          )}
                        </div>
                      </TD>
                      <TD
                        size="lg"
                        num
                        className="whitespace-nowrap text-v2-fg-2"
                      >
                        {formatPhone(link.customer_phone) || <MissingValue />}
                      </TD>
                      <TD size="lg" className="overflow-hidden">
                        <div className="flex min-w-0 items-center gap-3">
                          <a
                            href={link.reservation_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex shrink-0 items-center gap-1 font-medium text-v2-accent hover:underline"
                          >
                            Open link
                            <IconExternal size={12} />
                          </a>
                          <span
                            className="truncate text-v2-12 text-v2-fg-4"
                            title={link.reservation_link}
                          >
                            {link.reservation_link}
                          </span>
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </div>
          {showPagination && (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={RESERVATIONS_PAGE_SIZE}
              onPage={setCurrentPage}
              noun="links"
            />
          )}
        </>
      )}
    </ReservationsList>
  );
};

export default ReservationLinksView;
